import useI18n from '@/shared/hooks/useI18n'
import { Button, GenericFullscreenDialog } from '@/common'
import { CheckCircleIcon } from '@/common/components/icons'
import { FinalizedReportDialogBody } from '@/shared/components'
import { useSession } from '@/shared/hooks/useSession'
import { useCallback, useMemo, useState } from 'react'
import { PRODUCTION_METHOD_TO_QPD } from '@/shared/config/config'
import { EmissionQualifyingParams } from '@/shared/api/EmissionQualifyingParamsModel'
import { FinalizedReportPreview, useUpdateFinalizedReport } from '@/shared/hooks/api/finalizedReports'
import { toast } from 'react-hot-toast'
import { DataQualityInfo, JustificationInfo, QualityAssuranceInfo } from '../constants'

const getInitialState = (initialState: FinalizedReportPreview) => {
  return {
    isMonitoringMethodologyCalculationBased: initialState.isMonitoringMethodologyCalculationBased,
    isMonitoringMethodologyMeasurementBased: initialState.isMonitoringMethodologyMeasurementBased,
    isMonitoringMethodologyOther: initialState.isMonitoringMethodologyOther,
    monitoringMethodologyCalculationBasedInfo:
      initialState.monitoringMethodologyCalculationBasedInfo,
    monitoringMethodologyMeasurementBasedInfo:
      initialState.monitoringMethodologyMeasurementBasedInfo,
    monitoringMethodologyOtherInfo: initialState.monitoringMethodologyOtherInfo,
    dataQualityInfo: initialState.dataQualityInfo,
    qualityAssuranceInfo: initialState.qualityAssuranceInfo,
    defaultValuesJustification: initialState.defaultValuesJustification
  }
}

export default function UpdateFinalizeReportDialog({
  isOpen,
  close,
  initialState,
  installationId,
  productionMethodCodes,
  finalizedReportId
}: {
  isOpen: boolean
  close: () => void
  initialState: FinalizedReportPreview
  installationId: string
  productionMethodCodes: string[]
  finalizedReportId: string
}) {
  const { t } = useI18n()
  const {
    session: {
      organization: { id: organizationId }
    }
  } = useSession()

  const { mutateAsync: updateFinalizedReport, isPending: isUpdatingFinalizedReport } =
    useUpdateFinalizedReport(organizationId, installationId, finalizedReportId)

  const qualifyingParametersToFill = useMemo(
    () => productionMethodCodes.map(code => PRODUCTION_METHOD_TO_QPD[code]).flat(),
    [productionMethodCodes]
  )

  const requiredQualifyingParamsCodes = useMemo(
    () => qualifyingParametersToFill.filter(param => param.required).map(param => param.code),
    [qualifyingParametersToFill]
  )

  const [qualifyingParams, setQualifyingParams] = useState<EmissionQualifyingParams>(
    initialState.emissionQualifyingParams
  )

  const [state, setState] = useState<{
    dataQualityInfo: DataQualityInfo | null
    qualityAssuranceInfo: QualityAssuranceInfo | null
    defaultValuesJustification: JustificationInfo[] | null
  }>(getInitialState(initialState))

  const { dataQualityInfo, qualityAssuranceInfo, defaultValuesJustification } = state

  const isQualifyingParamsValid = qualifyingParametersToFill.every(param => {
    if (param.required) {
      return qualifyingParams[param.code as keyof typeof qualifyingParams]?.value !== null
    }
    return true
  })

  const isUpdateFinalizedReportButtonEnabled =
    isQualifyingParamsValid &&
    dataQualityInfo &&
    qualityAssuranceInfo &&
    dataQualityInfo.length > 0 &&
    qualityAssuranceInfo.length > 0

  const onClose = useCallback(() => {
    setQualifyingParams(initialState.emissionQualifyingParams)
    setState(getInitialState(initialState))
    close()
  }, [initialState, close])

  const onUpdateFinalizedReport = useCallback(async () => {
    try {
      await updateFinalizedReport({
        dataQualityInfo,
        qualityAssuranceInfo,
        defaultValuesJustification,
        emissionQualifyingParams: qualifyingParams
      })
      toast.success(
        t('reports:finalizedReports.updateFinalizeReportDialog.toast.updatedSuccessfully')
      )
      close()
    } catch {
      toast.error(t('reports:finalizedReports.updateFinalizeReportDialog.toast.failedToUpdate'))
    }
  }, [
    dataQualityInfo,
    qualityAssuranceInfo,
    defaultValuesJustification,
    qualifyingParams,
    updateFinalizedReport,
    close,
    t
  ])

  return (
    <GenericFullscreenDialog
      title={t('reports:finalizedReports.updateFinalizeReportDialog.title')}
      subtitle={t('reports:finalizedReports.updateFinalizeReportDialog.subtitle')}
      icon={<CheckCircleIcon className="text-green-600 size-6" />}
      isOpen={isOpen}
      close={onClose}
      preventCloseOnOutsideClick
      actions={
        <div className="flex justify-end gap-2">
          <Button variant="neutral" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            isLoading={isUpdatingFinalizedReport}
            onClick={onUpdateFinalizedReport}
            disabled={!isUpdateFinalizedReportButtonEnabled || isUpdatingFinalizedReport}
          >
            {t('common:actions.update')}
          </Button>
        </div>
      }
    >
      <FinalizedReportDialogBody
        requiredQualifyingParamsCodes={requiredQualifyingParamsCodes}
        productionMethodCodes={productionMethodCodes}
        state={state}
        qualifyingParams={qualifyingParams}
        setQualifyingParams={setQualifyingParams}
        updateState={setState}
      />
    </GenericFullscreenDialog>
  )
}
